import { useState } from 'react';
import { Icon } from '../../components/Icon.tsx';
import { Button, Dialog, EmptyState, Field, IconButton, Skeleton, StatusBadge } from '../../components/ui.tsx';
import { t, tn, type MessageKey } from '../../i18n/index.ts';
import { action } from '../../lib/api.ts';
import { dateTime, day, money, qty as fmtQty } from '../../lib/format.ts';
import { useSession } from '../../lib/session.tsx';
import type { Place } from '../../lib/types.ts';
import { useAction, useSave } from './common.tsx';
import { parseQty, withUnit, type Unit } from './Stock.tsx';

/** Lots and serial numbers: entering them on goods received, finding them, tracing them. */

export type Tracking = 'none' | 'lot' | 'serial';
export interface MoveLot {
  lot: string;
  code: string;
  qty: number;
}
interface LotRow {
  id: string;
  item: string;
  item_name: string;
  unit: Unit;
  tracking: Tracking;
  code: string;
  expires: string | null;
  on_hand: number;
  value: number;
  state: 'ok' | 'expiring' | 'expired';
  supplier: string | null;
  received_at: string;
  by_location: { location: string; on_hand: number }[];
}
interface Trace {
  lot: LotRow & { note: string | null };
  item: { id: string; name: string; unit: Unit; tracking: Tracking };
  by_location: { location: string; on_hand: number }[];
  summary: {
    received: number;
    sold: number;
    used: number;
    wasted: number;
    returned: number;
  };
  moves: {
    id: string;
    kind: string;
    at: string;
    qty: number;
    document: string | null;
    reason: string | null;
    order_name: string | null;
    receipt_no: number | null;
    location_name: string | null;
    by_name: string | null;
  }[];
}

/** "Y-12 · 3 kg, SN-4" for a movement's lots. */
export const lotsText = (lots: MoveLot[] | null | undefined) => (lots ?? []).map((l) => l.code).join(', ');

export function LotBadge({ state, expires }: { state: LotRow['state']; expires: string | null }) {
  if (!expires) return null;
  if (state === 'expired') return <StatusBadge tone="danger" icon="alert" label={t('lots.expired_on', { date: day(expires) })} />;
  if (state === 'expiring') return <StatusBadge tone="warn" icon="clock" label={t('lots.expires_on', { date: day(expires) })} />;
  return <StatusBadge tone="neutral" icon="calendar" label={t('lots.expires_on', { date: day(expires) })} />;
}

/* ------------------------------------------------------------ Entering lots on goods coming in */

export interface LotDraft {
  /** Lot tracking: one row per lot in the delivery. */
  rows: { code: string; qty: string; expires: string }[];
  /** Serial tracking: one number per line. */
  serials: string;
}
export const emptyLots = (): LotDraft => ({
  rows: [{ code: '', qty: '', expires: '' }],
  serials: '',
});

/** What the server takes for the lots typed, or why it is not ready yet. */
export function readLots(
  tracking: Tracking,
  qty: number | null,
  d: LotDraft,
): {
  lots?: { code: string; qty: number; expires?: string | null }[];
  problem?: string;
} {
  if (tracking === 'none' || !qty) return {};
  if (tracking === 'serial') {
    const codes = d.serials
      .split(/[\n,;]+/)
      .map((s) => s.trim())
      .filter(Boolean);
    if (new Set(codes.map((c) => c.toUpperCase())).size !== codes.length) return { problem: t('lots.serial_twice') };
    if (codes.length !== qty)
      return {
        problem: t('lots.serials_count', { n: codes.length, qty: fmtQty(qty) }),
      };
    return { lots: codes.map((code) => ({ code, qty: 1 })) };
  }
  const rows = d.rows.filter((r) => r.code.trim() || r.qty || r.expires);
  if (!rows.length || rows.some((r) => !r.code.trim())) return { problem: t('lots.code_needed') };
  // One lot takes the whole line; several share it.
  const lots = rows.map((r) => ({
    code: r.code.trim(),
    qty: rows.length === 1 ? qty : (parseQty(r.qty) ?? 0),
    expires: r.expires || null,
  }));
  if (lots.some((l) => !(l.qty > 0))) return { problem: t('lots.qty_needed') };
  const sum = Math.round(lots.reduce((s, l) => s + l.qty, 0) * 1000) / 1000;
  if (sum !== qty)
    return {
      problem: t('lots.sum_off', { sum: fmtQty(sum), qty: fmtQty(qty) }),
    };
  return { lots };
}

/** The lot numbers (and dates) or serial numbers of one line of goods received. */
export function LotsField({
  name,
  unit,
  tracking,
  qty,
  value,
  onChange,
}: {
  name: string;
  unit: Unit;
  tracking: Tracking;
  qty: number | null;
  value: LotDraft;
  onChange: (d: LotDraft) => void;
}) {
  if (tracking === 'none') return null;
  const { problem } = readLots(tracking, qty, value);
  if (tracking === 'serial') {
    return (
      <div className="lot-entry">
        <Field label={t('lots.serials_for', { name })} hint={problem ?? t('lots.serials_hint')}>
          <textarea
            className={`input lot-serials${problem && value.serials ? ' invalid' : ''}`}
            rows={3}
            value={value.serials}
            onChange={(e) => onChange({ ...value, serials: e.target.value })}
            placeholder="SN-0001"
          />
        </Field>
      </div>
    );
  }
  const several = value.rows.length > 1;
  const set = (i: number, k: 'code' | 'qty' | 'expires', v: string) =>
    onChange({
      ...value,
      rows: value.rows.map((r, j) => (j === i ? { ...r, [k]: v } : r)),
    });
  return (
    <div className="lot-entry" role="group" aria-label={t('lots.lots_for', { name })}>
      {value.rows.map((r, i) => (
        <div key={i} className={`lot-entry-row${several ? ' several' : ''}`}>
          <Field label={i === 0 ? t('lots.lot_no') : ''}>
            <input
              className="input"
              value={r.code}
              maxLength={60}
              aria-label={t('lots.lot_no')}
              onChange={(e) => set(i, 'code', e.target.value)}
              placeholder="L-2611"
            />
          </Field>
          {several ? (
            <Field label={i === 0 ? t('stock.quantity') : ''}>
              <span className="input-unit">
                <input
                  className="input"
                  inputMode="decimal"
                  aria-label={t('stock.quantity')}
                  value={r.qty}
                  onChange={(e) => set(i, 'qty', e.target.value)}
                  placeholder="0"
                />
                <span>{t(`stock.unit_${unit}` as MessageKey)}</span>
              </span>
            </Field>
          ) : null}
          <Field label={i === 0 ? t('lots.expires') : ''}>
            <input className="input" type="date" aria-label={t('lots.expires')} value={r.expires} onChange={(e) => set(i, 'expires', e.target.value)} />
          </Field>
          {several ? (
            <IconButton
              icon="trash"
              label={t('lots.remove_lot')}
              onClick={() =>
                onChange({
                  ...value,
                  rows: value.rows.filter((_, j) => j !== i),
                })
              }
            />
          ) : null}
        </div>
      ))}
      <div className="lot-entry-foot">
        <Button
          size="sm"
          variant="ghost"
          icon="plus"
          onClick={() =>
            onChange({
              ...value,
              rows: [...value.rows, { code: '', qty: '', expires: '' }],
            })
          }
        >
          {t('lots.add_lot')}
        </Button>
        {problem && value.rows.some((r) => r.code || r.qty) ? <span className="lot-problem">{problem}</span> : null}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------ Picking a lot (waste) */

export function LotPick({ item, location, value, onChange }: { item: string; location?: string; value: string; onChange: (v: string) => void }) {
  const { data } = useAction<{ lots: LotRow[] }>('inventory.lots', {
    item,
    ...(location ? { location } : {}),
  });
  return (
    <Field label={t('lots.which_lot')}>
      <select className="input" value={value} onChange={(e) => onChange(e.target.value)}>
        <option value="">{t('lots.first_expiring')}</option>
        {(data?.lots ?? []).map((l) => (
          <option key={l.id} value={l.id}>
            {[l.code, l.expires ? t('lots.expires_on', { date: day(l.expires) }) : null, withUnit(l.on_hand, l.unit)].filter(Boolean).join(' · ')}
          </option>
        ))}
      </select>
    </Field>
  );
}

/* ------------------------------------------------------------ Finding and tracing lots */

type LotFilter = 'in_stock' | 'expiring' | 'all';

export function LotsDialog({
  places,
  location,
  item,
  start = 'in_stock',
  onClose,
}: {
  places: Place[];
  location?: string;
  item?: { id: string; name: string };
  start?: LotFilter;
  onClose: () => void;
}) {
  const [filter, setFilter] = useState<LotFilter>(start);
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState<string | null>(null);
  const { data } = useAction<{
    lots: LotRow[];
    more: boolean;
    expiring_days: number;
  }>('inventory.lots', {
    filter,
    ...(location ? { location } : {}),
    ...(item ? { item: item.id } : {}),
    ...(search.trim() ? { search: search.trim() } : {}),
  });
  const place = (id: string) => places.find((p) => p.id === id);
  if (open) return <LotTrace lot={open} places={places} onClose={() => setOpen(null)} back />;
  return (
    <Dialog title={item ? t('lots.title_item', { name: item.name }) : t('lots.title')} onClose={onClose} size="lg">
      <div className="form-stack">
        <p className="muted small">{t('lots.hint', { days: data?.expiring_days ?? 30 })}</p>
        <div className="toolbar">
          <div className="segmented" role="radiogroup" aria-label={t('stock.show')}>
            {(['in_stock', 'expiring', 'all'] as const).map((f) => (
              <button key={f} role="radio" aria-checked={filter === f} className={filter === f ? 'active' : ''} onClick={() => setFilter(f)}>
                {t(`lots.f_${f}`)}
              </button>
            ))}
          </div>
          <div className="search grow">
            <Icon name="search" size={18} />
            <input
              className="input"
              type="search"
              placeholder={t('lots.search')}
              aria-label={t('lots.search')}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>
        <div className="list-card">
          {!data ? (
            <Skeleton h={160} />
          ) : !data.lots.length ? (
            <EmptyState
              icon="hash"
              title={filter === 'expiring' ? t('lots.none_expiring') : t('lots.none')}
              body={filter === 'expiring' ? undefined : t('lots.none_body')}
            />
          ) : (
            data.lots.map((l) => (
              <button key={l.id} className="list-row lot-row" onClick={() => setOpen(l.id)}>
                <span className={`list-icon lot-icon ${l.state}`} aria-hidden="true">
                  <Icon name="hash" size={18} />
                </span>
                <span className="list-main">
                  <span className="list-title">
                    <span className="mono">{l.code}</span>
                    {item ? null : <span className="muted"> · {l.item_name}</span>}
                  </span>
                  <span className="list-sub">
                    {places.length > 1 && l.by_location.length
                      ? l.by_location.map((b) => (
                          <span key={b.location} className="place-split-item">
                            <span className="place-tag">{place(b.location)?.code ?? '—'}</span> {withUnit(b.on_hand, l.unit)}
                          </span>
                        ))
                      : [
                          l.supplier,
                          t('lots.received_on', {
                            date: day(l.received_at.slice(0, 10)),
                          }),
                        ]
                          .filter(Boolean)
                          .join(' · ')}
                  </span>
                </span>
                <span className="stock-qty">
                  <strong className={l.on_hand <= 0 ? 'muted' : ''}>{withUnit(l.on_hand, l.unit)}</strong>
                  <span className="muted">{money(l.value)}</span>
                </span>
                <LotBadge state={l.state} expires={l.expires} />
              </button>
            ))
          )}
        </div>
        {data?.more ? <p className="muted small">{t('lots.more')}</p> : null}
      </div>
    </Dialog>
  );
}

const KIND: Record<string, MessageKey> = {
  sale: 'stock.kind_sale',
  purchase: 'stock.kind_purchase',
  waste: 'stock.kind_waste',
  count: 'stock.kind_count',
  transfer: 'stock.kind_transfer',
  use: 'stock.kind_use',
  consume: 'stock.kind_consume',
  produce: 'stock.kind_produce',
};

/** One lot: where it came from, where every piece went, and its date. */
export function LotTrace({ lot, places, onClose, back }: { lot: string; places: Place[]; onClose: () => void; back?: boolean }) {
  const { can } = useSession();
  const { data, reload } = useAction<Trace>('inventory.lot', { lot });
  const { busy, run } = useSave();
  const [edit, setEdit] = useState<{ expires: string; note: string } | null>(null);
  const save = () =>
    void run(async () => {
      await action('inventory.update_lot', {
        lot,
        expires: edit!.expires || null,
        note: edit!.note.trim() || null,
      });
      setEdit(null);
      await reload();
    }, t('lots.saved'));
  const u = data?.item.unit;
  return (
    <Dialog
      title={
        data ? t(data.item.tracking === 'serial' ? 'lots.serial_title' : 'lots.lot_title', { code: data.lot.code, name: data.item.name }) : t('lots.title')
      }
      onClose={onClose}
      size="lg"
      footer={
        back ? (
          <Button variant="ghost" icon="back" onClick={onClose}>
            {t('lots.back')}
          </Button>
        ) : undefined
      }
    >
      {!data || !u ? (
        <Skeleton h={240} />
      ) : (
        <div className="form-stack">
          <div className="item-summary">
            <div>
              <span className="muted">{t('stock.on_hand')}</span>
              <strong>{withUnit(data.lot.on_hand, u)}</strong>
            </div>
            <div>
              <span className="muted">{t('lots.expires')}</span>
              <strong>{data.lot.expires ? day(data.lot.expires) : '—'}</strong>
            </div>
            <div>
              <span className="muted">{t('lots.supplier')}</span>
              <strong>{data.lot.supplier ?? '—'}</strong>
            </div>
            <LotBadge state={data.lot.state} expires={data.lot.expires} />
          </div>
          {places.length > 1 && data.by_location.length ? (
            <span className="place-split">
              {data.by_location.map((b) => (
                <span key={b.location} className="place-split-item">
                  <span className="place-tag">{places.find((p) => p.id === b.location)?.code ?? '—'}</span> {withUnit(b.on_hand, u)}
                </span>
              ))}
            </span>
          ) : null}
          <dl className="lot-flow" aria-label={t('lots.flow')}>
            {(['received', 'sold', 'used', 'wasted', 'returned'] as const).map((k) =>
              data.summary[k] || k === 'received' || k === 'sold' ? (
                <div key={k}>
                  <dt>{t(`lots.sum_${k}`)}</dt>
                  <dd>{withUnit(data.summary[k], u)}</dd>
                </div>
              ) : null,
            )}
          </dl>
          {data.lot.note && !edit ? <p className="muted">{data.lot.note}</p> : null}
          {can('inventory.manage') ? (
            edit ? (
              <div className="waste-form">
                <div className="form-grid">
                  <Field label={t('lots.expires')}>
                    <input className="input" type="date" value={edit.expires} onChange={(e) => setEdit({ ...edit, expires: e.target.value })} data-autofocus />
                  </Field>
                  <Field label={t('lots.note')}>
                    <input className="input" value={edit.note} maxLength={200} onChange={(e) => setEdit({ ...edit, note: e.target.value })} />
                  </Field>
                </div>
                <div className="form-actions">
                  <Button variant="ghost" onClick={() => setEdit(null)}>
                    {t('common.cancel')}
                  </Button>
                  <Button variant="primary" icon="check" onClick={save} busy={busy}>
                    {t('common.save')}
                  </Button>
                </div>
              </div>
            ) : (
              <div>
                <Button
                  size="sm"
                  icon="edit"
                  onClick={() =>
                    setEdit({
                      expires: data.lot.expires ?? '',
                      note: data.lot.note ?? '',
                    })
                  }
                >
                  {t('lots.edit')}
                </Button>
              </div>
            )
          ) : null}
          <section aria-labelledby="lot-trace-title">
            <h3 id="lot-trace-title" className="section-title">
              {t('lots.trace')}
            </h3>
            <p className="muted small">{tn('lots.trace_hint', data.moves.length)}</p>
            <div className="list-card">
              {data.moves.map((m) => (
                <div key={m.id} className="list-row">
                  <span className={`list-icon move-${m.kind}`} aria-hidden="true">
                    <Icon name={m.qty > 0 ? 'plus' : m.kind === 'waste' ? 'trash' : m.kind === 'transfer' ? 'transfer' : 'receipt'} size={18} />
                  </span>
                  <span className="list-main">
                    <span className="list-title">{t(KIND[m.kind] ?? 'stock.kind_count')}</span>
                    <span className="list-sub">
                      {[
                        dateTime(m.at),
                        m.location_name,
                        m.kind === 'sale' && m.order_name
                          ? t('stock.sold_on', {
                              name: m.order_name,
                              n: m.receipt_no ?? '',
                            })
                          : [m.document, m.reason].filter(Boolean).join(' · '),
                        m.by_name,
                      ]
                        .filter(Boolean)
                        .join(' · ')}
                    </span>
                  </span>
                  <span className="stock-qty">
                    <strong className={m.qty < 0 ? '' : 'ok'}>
                      {m.qty > 0 ? '+' : '−'}
                      {withUnit(Math.abs(m.qty), u)}
                    </strong>
                  </span>
                </div>
              ))}
            </div>
          </section>
        </div>
      )}
    </Dialog>
  );
}
